import React, { useState } from "react";
import axios from "axios";
import { Send, CheckCircle2, Phone, Mail, MapPin } from "lucide-react";
import { useT } from "@/i18n";

// Use relative /api path so form works with any backend on the same domain (Hostinger, etc.)
// In Emergent preview it falls through to the REACT_APP_BACKEND_URL via runtime detection.
const API = (process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api");

const Quote = () => {
  const { t } = useT();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", project_type: "", etch_depth: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/quote`, form, { timeout: 8000 });
      setStatus("sent");
    } catch (err) {
      // Fallback: open visitor's email client with the message pre-filled to info@milysuperior.com
      const subject = encodeURIComponent(`Quote Request — ${form.name || "Website Visitor"}`);
      const bodyLines = [
        `Name: ${form.name}`,
        `Email: ${form.email}`,
        `Phone: ${form.phone}`,
        `Company: ${form.company}`,
        `Project Type: ${form.project_type}`,
        `Target Etch Depth: ${form.etch_depth}`,
        "",
        "Details:",
        form.message,
      ];
      const body = encodeURIComponent(bodyLines.join("\n"));
      window.location.href = `mailto:info@milysuperior.com?subject=${subject}&body=${body}`;
      setStatus("sent");
    }
  };

  const f = t.quote.fields;

  return (
    <section id="quote" data-testid="quote-section" className="relative py-24 sm:py-32 bg-[#faf7f2]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-6">{t.quote.overline}</div>
            <h2 className="font-display text-4xl sm:text-6xl tracking-tight leading-[1.02] mb-8">
              {t.quote.h}<br />{t.quote.h2}
            </h2>
            <p className="text-zinc-600 leading-relaxed mb-8 max-w-md">{t.quote.sub}</p>
            <div className="relative aspect-[5/3] overflow-hidden mb-8 group">
              <img
                src="/generated/patio_finish.png"
                alt="Real Mily Superior decorative concrete project"
                className="w-full h-full object-cover transition-transform duration-[2000ms] group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
              <div className="absolute bottom-4 left-5 text-white">
                <div className="overline !text-white">{t.quote.realProjects}</div>
                <div className="font-display text-lg mt-1 text-white">{t.quote.builtCaption}</div>
              </div>
            </div>

            <div className="border-t border-black/10 pt-8 space-y-6">
              <div>
                <div className="overline mb-3">{t.quote.callUs}</div>
                <a
                  href="tel:5145941482"
                  data-testid="quote-call-button"
                  className="inline-flex items-center gap-3 font-display text-3xl hover:text-[#ea7a1f] transition-colors"
                >
                  <Phone className="w-6 h-6 text-[#ea7a1f]" />
                  514-594-1482
                </a>
              </div>
              <div>
                <div className="overline mb-3">{t.quote.emailUs}</div>
                <a
                  href="mailto:info@milysuperior.com"
                  data-testid="quote-email-link"
                  className="inline-flex items-center gap-3 font-display text-2xl hover:text-[#ea7a1f] transition-colors break-all"
                >
                  <Mail className="w-5 h-5 text-[#ea7a1f] shrink-0" />
                  info@milysuperior.com
                </a>
              </div>
              <div>
                <div className="overline mb-3">{t.quote.visitUs}</div>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=1366+Avenue+De+La+Gare+Mascouche+QC+J7K+2Z2"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="quote-address-link"
                  className="flex items-start gap-3 font-display text-xl hover:text-[#ea7a1f] transition-colors leading-snug"
                >
                  <MapPin className="w-5 h-5 text-[#ea7a1f] mt-1 shrink-0" />
                  <span>
                    1366 Avenue De La Gare<br />
                    Mascouche, Quebec J7K 2Z2<br />
                    <span className="text-sm text-zinc-500 tracking-wide">{t.quote.serving}</span>
                  </span>
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {status === "sent" ? (
              <div
                data-testid="quote-success"
                className="border border-[#ea7a1f]/40 bg-[#ea7a1f]/5 p-12 flex flex-col items-start gap-6"
              >
                <CheckCircle2 className="w-12 h-12 text-[#ea7a1f]" strokeWidth={1.5} />
                <div>
                  <h3 className="font-display text-3xl mb-3">{t.quote.successH}</h3>
                  <p className="text-zinc-600 max-w-md">
                    {t.quote.successP(form.name)} <a href="tel:5145941482" className="text-[#ea7a1f] font-semibold">514-594-1482</a>.
                  </p>
                </div>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="quote-form"
                className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-black/10 border border-black/10"
              >
                <Field label={f.name} name="name" required value={form.name} onChange={onChange} testid="quote-name" />
                <Field label={f.company} name="company" value={form.company} onChange={onChange} testid="quote-company" />
                <Field label={f.email} name="email" type="email" required value={form.email} onChange={onChange} testid="quote-email" />
                <Field label={f.phone} name="phone" type="tel" value={form.phone} onChange={onChange} testid="quote-phone" />
                <Field label={f.project} name="project_type" value={form.project_type} onChange={onChange} testid="quote-project-type" placeholder={f.projectPh} />
                <Field label={f.etch} name="etch_depth" value={form.etch_depth} onChange={onChange} testid="quote-etch-depth" placeholder={f.etchPh} />
                <div className="sm:col-span-2 bg-[#faf7f2] p-6">
                  <label className="block overline mb-3">{f.details}</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={4}
                    data-testid="quote-message"
                    className="w-full bg-transparent border-b border-black/10 focus:border-[#ea7a1f] outline-none py-2 text-[#14141a] placeholder-zinc-400 resize-none transition-colors"
                    placeholder={f.detailsPh}
                  />
                </div>
                <div className="sm:col-span-2 bg-[#faf7f2] p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  {error && <span className="text-red-500 text-sm" data-testid="quote-error">{error}</span>}
                  <span className="text-zinc-500 text-xs tracking-[0.2em] uppercase">{t.quote.reply}</span>
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="quote-submit"
                    className="group inline-flex items-center gap-3 bg-[#ea7a1f] hover:bg-[#f08b34] text-white font-bold px-6 py-4 transition-all disabled:opacity-60"
                  >
                    {status === "sending" ? t.quote.sending : t.quote.send}
                    <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, type = "text", required, placeholder, testid }) => (
  <div className="bg-[#faf7f2] p-6">
    <label className="block overline mb-3">{label}{required && " *"}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-transparent border-b border-black/10 focus:border-[#ea7a1f] outline-none py-2 text-[#14141a] placeholder-zinc-400 transition-colors"
    />
  </div>
);

export default Quote;
